import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  inject
} from '@angular/core';
import { AdditionalInfo } from '@models/additional-info.model';
import { AdditionalInfoService } from '@services/additional-info.service';
import { ErrorService } from '@services/error.service';

@Component({
  selector: 'app-additional-document-request',
  templateUrl: './additional-document-request.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdditionalDocumentRequestComponent {
  @Input() additionInfo!: AdditionalInfo;
  @Output() closeModalEvent = new EventEmitter();
  protected additionalService = inject(AdditionalInfoService);
  protected notUpdateSuccess: boolean = true;
  uploadError: boolean = false;
  isValidFile: boolean = false;
  fileChangedEvent: any;
  preViewImage: any = '';
  isShowEnlargeImage: boolean = false;
  FILE_TYPE: string[] = ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx'];
  MAX_SIZE = 5 * 1024 * 1024; // 5MB
  fileInfo = {
    name: '',
    type: ''
  };
  errorMsg: string = '';
  files: Array<File> = [];
  imageFiles: Array<File> = [];
  otherFiles: Array<File> = [];
  protected isWebview = false;

  private readonly errorService = inject(ErrorService);
  readonly unexpectedError$ = this.errorService.unexpectedError$;

  constructor(private cdr: ChangeDetectorRef) {}
  isFileAllowed(file: File): boolean {
    const fileExtension = this.getFileExtension(file.name);
    return this.FILE_TYPE.includes(fileExtension);
  }

  private getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toLowerCase() || '';
  }

  processFile = (event: any) => {
    var file = event.target.files[0];
    this.checkFile(file);
  };
  async checkFile(file: File) {
    this.uploadError = false;
    this.errorMsg = '';
    if (this.isFileAllowed(file)) {
      const base64 = await this.convertBase64(file);
      if (file.type.includes('image')) {
        this.preViewImage = base64;
      } else {
        if (
          this.getFileExtension(file.name) === 'doc' ||
          this.getFileExtension(file.name) === 'docx'
        ) {
          this.fileInfo.name = file.name;
          this.fileInfo.type = 'DOC';
        } else if (this.getFileExtension(file.name) === 'pdf') {
          this.fileInfo.name = file.name;
          this.fileInfo.type = 'PDF';
        }
      }

      if (file.size > this.MAX_SIZE) {
        this.uploadError = true;
        this.errorMsg = 'Maximum 5mb of file size';
        this.cdr.markForCheck();
        return;
      }
      this.fileChangedEvent = event;
      if (file.type.includes('image')) {
        this.imageFiles.push(file);
      } else {
        this.otherFiles.push(file);
      }
      this.isValidFile = true;
      this.files.push(file);
      this.cdr.markForCheck();
    } else {
      this.uploadError = true;
      this.errorMsg = 'This file type is not supported';
      this.cdr.markForCheck();
      return;
    }
  }
  filesDropped(files: File[]) {
    const file = files[0];
    this.checkFile(file);
  }
  convertBase64 = (file: any) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.onerror = error => {
        reject(error);
      };
    });
  };

  closeModal() {
    this.closeModalEvent.emit();
  }

  submitFiles() {
    const files = [...this.imageFiles, ...this.otherFiles];
    if (files.length) {
      const formData = new FormData();
      formData.append(
        'additional_file_request_id',
        this.additionInfo.additional_file_request!.id.toString()
      );
      this.files.forEach(file => formData.append('documents[]', file));
      this.additionalService.uploadAdditionalFiles(formData).subscribe(res => {
        this.notUpdateSuccess = false;
        this.cdr.markForCheck();
      });
    }
  }

  updateSuccesfully() {
    this.closeModalEvent.emit();
  }
  previewImage(file: any) {
    return URL.createObjectURL(file);
  }

  removeImageFiles(file: number) {
    const deleteIndex = this.files.indexOf(this.imageFiles[file]);
    this.imageFiles.splice(file, 1);
    this.files.splice(deleteIndex, 1);
    this.cdr.markForCheck();
  }

  removeOtherFiles(file: number) {
    const deleteIndex = this.files.indexOf(this.otherFiles[file]);
    this.otherFiles.splice(file, 1);
    this.files.splice(deleteIndex, 1);
  }

  formatExtention(file: any) {
    let type = '';
    if (this.getFileExtension(file.name) === 'doc' || this.getFileExtension(file.name) === 'docx') {
      type = 'DOC';
    } else if (this.getFileExtension(file.name) === 'pdf') {
      type = 'PDF';
    }
    return type;
  }

  ngOnInit() {
    const userAgent = navigator.userAgent.toLowerCase();
    let safari = /safari/.test(userAgent);
    let ios = /iphone|ipod|ipad/.test(userAgent);
    if (userAgent.indexOf('wv') > -1 || userAgent.indexOf('webview') > -1 || (!safari && ios)) {
      this.isWebview = true;
    }
  }
}
